import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Moon, Dumbbell, Trash2, CheckCircle2, XCircle, Plus, Copy } from "lucide-react";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ds = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const chipTone = (a) =>
  a.is_rest ? "bg-white/10 text-tertiary"
  : a.status === "completed" ? "bg-success/20 text-success"
  : a.status === "missed" ? "bg-brand/20 text-brand"
  : "bg-brand/10 text-secondary";

export default function ProgramCalendar({ assignments = [], templates = [], onRemove, onAdd, onCopyWeek }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(null);
  const [addSel, setAddSel] = useState("");
  const [copyWeeks, setCopyWeeks] = useState(3);
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekStartOf = (dateStr) => { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };

  const byDate = useMemo(() => {
    const map = {};
    assignments.forEach((a) => { (map[a.scheduled_date] ||= []).push(a); });
    return map;
  }, [assignments]);

  const { y, m } = cursor;
  const startWeekday = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(startWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const move = (delta) => {
    setSelected(null);
    setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  };
  const goToday = () => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); setSelected(null); };

  const selItems = selected ? byDate[selected] || [] : [];

  return (
    <div data-testid="program-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-2xl">{MONTHS[m]} {y}</h3>
        <div className="flex items-center gap-2">
          <button data-testid="cal-today" onClick={goToday} className="px-3 py-1.5 text-xs border border-white/15 rounded-lg hover:border-white">Today</button>
          <button data-testid="cal-prev" onClick={() => move(-1)} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center hover:border-white"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="cal-next" onClick={() => move(1)} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center hover:border-white"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAY_LABELS.map((w) => <div key={w} className="text-center label-xs py-1">{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const date = ds(y, m, d);
          const items = byDate[date] || [];
          const isToday = date === todayStr;
          return (
            <button key={date} data-testid={`cal-day-${date}`} onClick={() => { setSelected(date); setAddSel(""); }}
              className={`min-h-[76px] rounded-lg border p-1.5 text-left align-top transition-colors cursor-pointer hover:border-brand ${isToday ? "border-brand" : "border-border"} ${selected === date ? "ring-1 ring-brand" : ""}`}>
              <div className={`text-xs font-num mb-1 ${isToday ? "text-brand font-bold" : "text-tertiary"}`}>{d}</div>
              <div className="space-y-0.5">
                {items.slice(0, 3).map((a) => (
                  <div key={a.id} className={`text-[10px] leading-tight px-1 py-0.5 rounded flex items-center gap-1 ${chipTone(a)}`}>
                    {a.is_rest ? <Moon className="w-2.5 h-2.5 flex-shrink-0" /> : a.status === "completed" ? <CheckCircle2 className="w-2.5 h-2.5 flex-shrink-0" /> : a.status === "missed" ? <XCircle className="w-2.5 h-2.5 flex-shrink-0" /> : <Dumbbell className="w-2.5 h-2.5 flex-shrink-0" />}
                    <span className="truncate">{a.is_rest ? "Rest" : a.workout_name}</span>
                  </div>
                ))}
                {items.length > 3 && <div className="text-[10px] text-tertiary pl-1">+{items.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 text-[11px] text-tertiary flex-wrap">
        <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3 text-secondary" /> Workout</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-success" /> Completed</span>
        <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-brand" /> Missed</span>
        <span className="flex items-center gap-1"><Moon className="w-3 h-3" /> Rest day</span>
      </div>

      {selected && (
        <div data-testid="cal-day-detail" className="mt-4 card-surface rounded-xl p-4">
          <p className="label-xs mb-2">{new Date(selected + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          <div className="space-y-2">
            {selItems.length === 0 && <p className="text-tertiary text-sm">Nothing scheduled this day.</p>}
            {selItems.map((a) => (
              <div key={a.id} className="flex items-center gap-3 border border-border rounded-lg p-2.5">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">{a.is_rest ? <Moon className="w-4 h-4 text-tertiary" /> : <Dumbbell className="w-4 h-4 text-brand" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{a.is_rest ? "Rest Day" : a.workout_name}</p>
                  <p className="text-xs text-tertiary capitalize">{a.status}{!a.is_rest && a.exercises ? ` · ${a.exercises.length} exercises` : ""}</p>
                </div>
                {onRemove && <button data-testid={`cal-remove-${a.id}`} onClick={() => onRemove(a)} className="p-1.5 text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>

          {onAdd && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/10">
              <select data-testid="cal-add-select" value={addSel} onChange={(e) => setAddSel(e.target.value)} className="flex-1 min-w-[160px] bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand">
                <option value="">{templates.length ? "Choose a workout…" : "No templates yet"}</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button data-testid="cal-add-workout" disabled={!addSel} onClick={() => { onAdd(selected, { workout_id: addSel }); setAddSel(""); }} className="px-3 py-2 bg-brand text-white rounded-lg text-sm font-semibold flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"><Plus className="w-4 h-4" /> Add workout</button>
              <button data-testid="cal-add-rest" onClick={() => onAdd(selected, { rest: true })} className="px-3 py-2 border border-white/15 rounded-lg text-sm font-semibold flex items-center gap-1 hover:border-white"><Moon className="w-4 h-4" /> Rest day</button>
            </div>
          )}

          {onCopyWeek && selItems.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-xs text-secondary mb-2 flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy the week of {new Date(weekStartOf(selected) + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} forward</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-secondary">Next</span>
                <input data-testid="cal-copy-weeks" type="number" min="1" max="52" value={copyWeeks} onChange={(e) => setCopyWeeks(e.target.value)} className="w-16 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:border-brand" />
                <span className="text-sm text-secondary">week(s)</span>
                <button data-testid="cal-copy-week" onClick={() => onCopyWeek(weekStartOf(selected), Number(copyWeeks) || 1)} className="px-3 py-2 border border-white/15 rounded-lg text-sm font-semibold flex items-center gap-1 hover:border-brand hover:text-brand"><Copy className="w-4 h-4" /> Copy week</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
